/**
 * policies-api — thin client for `/api/admin/business-policies/*`
 * (ADR-103 §3 storage + §7 lifecycle + §8 conflict resolution).
 *
 * Mock is localStorage-backed. The simulator embeds a deliberately small
 * rules engine for the 8 seed policies — enough to demonstrate trace UX,
 * not a substitute for the real evaluator (which owns the DSL).
 */
import { APP_ENV } from '../../shared/config/env';
import * as httpApi from './policies-api-http';
import { SEED_EVALUATIONS, SEED_OVERRIDES, SEED_POLICIES } from './seeds';
import type {
  PolicyEvaluationRecord,
  PolicyLifecycleAction,
  PolicyOverrideFormValues,
  PolicyRecord,
  PolicyTenantOverride,
  SimulatorConditionTrace,
  SimulatorResult,
} from './types';

const useLiveApi = APP_ENV.bizApiMode === 'live';

interface Store {
  policies: PolicyRecord[];
  evaluations: Record<string, PolicyEvaluationRecord[]>;
  overrides: Record<string, PolicyTenantOverride[]>;
}

const STORAGE_KEY = 'petota-business:admin-policies-mock';

function readStore(): Store {
  if (typeof window === 'undefined') {
    return {
      policies: SEED_POLICIES,
      evaluations: SEED_EVALUATIONS,
      overrides: SEED_OVERRIDES,
    };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seeded: Store = {
        policies: SEED_POLICIES,
        evaluations: SEED_EVALUATIONS,
        overrides: SEED_OVERRIDES,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    return JSON.parse(raw) as Store;
  } catch {
    return {
      policies: SEED_POLICIES,
      evaluations: SEED_EVALUATIONS,
      overrides: SEED_OVERRIDES,
    };
  }
}

function writeStore(store: Store): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function listPolicies(): Promise<PolicyRecord[]> {
  if (useLiveApi) return httpApi.listPolicies();
  await delay(180);
  return readStore().policies;
}

export async function getPolicy(policyId: string): Promise<PolicyRecord | null> {
  if (useLiveApi) return httpApi.getPolicy(policyId);
  await delay(160);
  return readStore().policies.find((p) => p.policyId === policyId) ?? null;
}

export async function listEvaluations(
  policyId: string,
): Promise<PolicyEvaluationRecord[]> {
  if (useLiveApi) return httpApi.listEvaluations(policyId);
  await delay(220);
  return readStore().evaluations[policyId] ?? [];
}

export async function listOverrides(
  policyId: string,
): Promise<PolicyTenantOverride[]> {
  if (useLiveApi) return httpApi.listOverrides(policyId);
  await delay(180);
  return readStore().overrides[policyId] ?? [];
}

/**
 * Update the policy's JSON document (raw editor).
 *
 * The real backend validates against the strict Zod schema + computes a
 * new checksum. The mock just stores whatever the UI submitted (after
 * the UI itself validated via Zod).
 */
export async function updatePolicyDocument(
  policyId: string,
  document: PolicyRecord['document'],
): Promise<PolicyRecord> {
  if (useLiveApi) return httpApi.updatePolicyDocument(policyId, document);
  await delay(320);
  const store = readStore();
  const idx = store.policies.findIndex((p) => p.policyId === policyId);
  if (idx < 0) throw new Error(`Policy not found: ${policyId}`);
  const current = store.policies[idx]!;
  const next: PolicyRecord = {
    ...current,
    name: document.name,
    description: document.description,
    scopeActions: document.scope.actions,
    scopeVerticalKeys: document.scope.subVerticalKeys,
    priority: document.priority,
    combinator: document.combinator,
    audit: document.audit,
    onDeny: document.onDeny,
    document,
    version: document.version,
    updatedAt: new Date().toISOString(),
    checksumSha256: `sha256-${policyId}-${Date.now()}`,
  };
  store.policies[idx] = next;
  writeStore(store);
  return next;
}

/**
 * Apply a lifecycle transition with a reason (ECTP Rule 7).
 * Valid transitions (ADR-103 §7):
 *   draft → shadow → active → deprecated → archived
 */
const VALID_TRANSITIONS: Record<PolicyRecord['status'], PolicyLifecycleAction[]> = {
  draft: ['shadow', 'activate'],
  shadow: ['activate', 'deprecate'],
  active: ['deprecate'],
  deprecated: ['archive'],
  archived: [],
};

export async function applyLifecycle(
  policyId: string,
  action: PolicyLifecycleAction,
  reason: string,
): Promise<PolicyRecord> {
  if (useLiveApi) return httpApi.applyLifecycle(policyId, action, reason);
  await delay(360);
  if (reason.trim().length < 10) {
    throw new Error('reason must be at least 10 characters');
  }
  const store = readStore();
  const idx = store.policies.findIndex((p) => p.policyId === policyId);
  if (idx < 0) throw new Error(`Policy not found: ${policyId}`);
  const current = store.policies[idx]!;
  if (!VALID_TRANSITIONS[current.status].includes(action)) {
    throw new Error(
      `Invalid transition ${action} from status ${current.status}`,
    );
  }
  const nowIso = new Date().toISOString();
  const statusMap: Record<PolicyLifecycleAction, PolicyRecord['status']> = {
    shadow: 'shadow',
    activate: 'active',
    deprecate: 'deprecated',
    archive: 'archived',
  };
  const nextStatus = statusMap[action];
  const next: PolicyRecord = {
    ...current,
    status: nextStatus,
    updatedAt: nowIso,
    activatedAt: action === 'activate' ? nowIso : current.activatedAt,
    deprecatedAt: action === 'deprecate' ? nowIso : current.deprecatedAt,
  };
  store.policies[idx] = next;
  writeStore(store);
  return next;
}

export async function createOverride(
  policyId: string,
  values: PolicyOverrideFormValues,
): Promise<PolicyTenantOverride> {
  if (useLiveApi) return httpApi.createOverride(policyId, values);
  await delay(280);
  const store = readStore();
  const record: PolicyTenantOverride = {
    id: `ov-${Date.now()}`,
    tenantId: values.tenantId,
    tenantName: values.tenantId.slice(0, 8),
    policyId,
    override: values.override,
    reason: values.reason,
    createdAt: new Date().toISOString(),
    createdBy: 'current-admin',
    expiresAt: values.expiresAt,
  };
  const list = store.overrides[policyId] ?? [];
  list.unshift(record);
  store.overrides[policyId] = list;
  writeStore(store);
  return record;
}

export async function revokeOverride(
  policyId: string,
  overrideId: string,
): Promise<void> {
  if (useLiveApi) return httpApi.revokeOverride(policyId, overrideId);
  await delay(200);
  const store = readStore();
  const list = store.overrides[policyId] ?? [];
  store.overrides[policyId] = list.filter((o) => o.id !== overrideId);
  writeStore(store);
}

/**
 * In-browser simulator — mock evaluator for the 8 seed policies.
 *
 * Each policy has a small hardcoded rule that inspects the operator-supplied
 * JSON context. The goal is *trace UX*, not evaluator fidelity.
 */
export async function simulatePolicy(
  policyId: string,
  contextJson: string,
): Promise<SimulatorResult> {
  if (useLiveApi) return httpApi.simulatePolicy(policyId, contextJson);
  const start = performance.now();
  await delay(320);
  const ctx = JSON.parse(contextJson) as Record<string, unknown>;
  const policy = await getPolicy(policyId);
  if (!policy) throw new Error(`Policy not found: ${policyId}`);

  const trace: SimulatorConditionTrace[] = [];
  let outcome: SimulatorResult['outcome'] = 'allow';

  const push = (
    conditionId: string,
    description: string,
    pass: boolean,
    explanation?: string,
  ) => {
    trace.push({
      conditionId,
      description,
      outcome: pass ? 'pass' : 'fail',
      explanation,
      durationMs: Math.round(Math.random() * 3 + 1),
    });
  };

  switch (policyId) {
    case 'POL-VET-001': {
      const certs = pickPath(ctx, 'profile.compliance.requiredCertifications');
      const hasCert = Array.isArray(certs) && certs.includes('vet_license');
      push('c1', 'profile.compliance includes vet_license', hasCert, String(certs));
      const license = pickPath(ctx, 'vetLicense') as
        | { expiresAt?: string; verified?: boolean }
        | undefined;
      const valid =
        Boolean(license?.expiresAt) && new Date(license!.expiresAt!) > new Date();
      const verified = license?.verified === true;
      push('c2', 'license.expires_at > now AND verified === true', valid && verified,
        `expiresAt=${license?.expiresAt ?? '(none)'}, verified=${String(license?.verified)}`);
      if (!(hasCert && valid && verified)) outcome = 'deny';
      break;
    }
    case 'POL-CUSTOMER-BLACKLIST': {
      const status = pickPath(ctx, 'customer.blacklistStatus');
      const ok = status === 'not_listed';
      push('c1', "customer.blacklistStatus === 'not_listed'", ok, String(status));
      if (!ok) outcome = 'deny';
      break;
    }
    case 'POL-GDPR-RETENTION': {
      const createdAt = pickPath(ctx, 'customer.createdAt') as string | undefined;
      const retentionDays = Number(
        pickPath(ctx, 'profile.compliance.dataRetentionDays') ?? 1825,
      );
      if (!createdAt) {
        push('c1', 'customer.createdAt present', false, 'missing createdAt');
        outcome = 'deny';
        break;
      }
      const ageDays = Math.floor(
        (Date.now() - new Date(createdAt).getTime()) / 86_400_000,
      );
      const ok = ageDays < retentionDays;
      push(
        'c1',
        'ageDays < profile.compliance.dataRetentionDays',
        ok,
        `ageDays=${ageDays}, retention=${retentionDays}`,
      );
      if (!ok) outcome = 'deny';
      break;
    }
    case 'POL-TENANT-TIER': {
      const moduleTier = String(pickPath(ctx, 'module.minTier') ?? 'basic');
      const tenantTier = String(pickPath(ctx, 'tenant.tier') ?? 'basic');
      const order = ['basic', 'pro', 'enterprise'];
      const ok = order.indexOf(tenantTier) >= order.indexOf(moduleTier);
      push(
        'c1',
        'tenant.tier ≥ module.minTier',
        ok,
        `tenantTier=${tenantTier}, moduleMinTier=${moduleTier}`,
      );
      if (!ok) outcome = 'deny';
      break;
    }
    case 'POL-CANCEL-WINDOW': {
      const startAt = pickPath(ctx, 'booking.startAt') as string | undefined;
      const windowHours = Number(
        pickPath(ctx, 'profile.settings.cancellationWindowHours') ?? 24,
      );
      if (!startAt) {
        push('c1', 'booking.startAt present', false, 'missing startAt');
        outcome = 'deny';
        break;
      }
      const diffMs = new Date(startAt).getTime() - Date.now();
      const hoursLeft = Math.floor(diffMs / 3_600_000);
      const ok = hoursLeft >= windowHours;
      push(
        'c1',
        'now before (booking.startAt - cancellationWindowHours)',
        ok,
        `hoursLeft=${hoursLeft}, windowHours=${windowHours}`,
      );
      if (!ok) outcome = 'deny';
      break;
    }
    case 'POL-MAX-DAILY-BOOKINGS': {
      const count = Number(pickPath(ctx, 'today.bookingCount') ?? 0);
      const limit = Number(
        pickPath(ctx, 'profile.scheduling.maxDailyBookings') ?? 20,
      );
      const ok = count < limit;
      push(
        'c1',
        'today.bookingCount < profile.scheduling.maxDailyBookings',
        ok,
        `count=${count}, limit=${limit}`,
      );
      if (!ok) outcome = 'deny';
      break;
    }
    case 'POL-PRESCRIPTION-REPEAT': {
      const count = Number(pickPath(ctx, 'prescription.sameDrugCount30d') ?? 0);
      const ok = count <= 1;
      push(
        'c1',
        'prescription.sameDrugCount30d <= 1',
        ok,
        `count=${count}`,
      );
      if (!ok) outcome = 'deny';
      break;
    }
    case 'POL-FINANCIAL-LIMIT': {
      const total = Number(pickPath(ctx, 'payouts.todayTotal') ?? 0);
      const limit = Number(pickPath(ctx, 'tenant.dailyPayoutLimit') ?? 50_000);
      const ok = total <= limit;
      push(
        'c1',
        'payouts.todayTotal <= tenant.dailyPayoutLimit',
        ok,
        `total=${total}, limit=${limit}`,
      );
      if (!ok) outcome = 'deny';
      break;
    }
    default: {
      trace.push({
        conditionId: 'c0',
        description: 'No mock simulator registered for this policy',
        outcome: 'error',
        explanation: 'Register a handler in simulatePolicy() to enable trace.',
        durationMs: 0,
      });
      outcome = 'error';
    }
  }

  const durationMs = Math.round(performance.now() - start);
  return {
    policyId,
    outcome,
    statusCode: outcome === 'deny' ? policy.onDeny.statusCode : undefined,
    errorCode: outcome === 'deny' ? policy.onDeny.errorCode : undefined,
    message: outcome === 'deny' ? policy.onDeny.message : undefined,
    durationMs,
    trace,
    simulatedAt: new Date().toISOString(),
  };
}

function pickPath(ctx: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let acc: unknown = ctx;
  for (const p of parts) {
    if (acc == null || typeof acc !== 'object') return undefined;
    acc = (acc as Record<string, unknown>)[p];
  }
  return acc;
}

export const policyKeys = {
  all: ['admin-business-policies'] as const,
  list: () => [...policyKeys.all, 'list'] as const,
  detail: (policyId: string) => [...policyKeys.all, 'detail', policyId] as const,
  evaluations: (policyId: string) =>
    [...policyKeys.all, 'evaluations', policyId] as const,
  overrides: (policyId: string) =>
    [...policyKeys.all, 'overrides', policyId] as const,
};
